import { useMemo } from 'react';

import { useIntl } from 'umi';
import { findLastIndex } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';

import {
  ISwitchAgentAccordionGroup,
  ISwitchAgentAccordionItem,
} from '@/pages/aigc/components/SwitchAgentAccordion';
import { convertSecondsToTimeFormat } from '@/pages/aigc/pages/Agent/ChatPage/utils';
import {
  getThinkFoldModeFromValue,
  isAgentFoldMode,
  normalizeAgentThinkItems,
  resolveAgentAccordionData,
} from '@/pages/aigc/pages/Agent/ChatPage/utils/thinkFoldAgent';

const getToolUseKey = (thinkId?: string, toolCallId?: string): string =>
  `${thinkId || ''}__${toolCallId || ''}`;

const INLINE_THINK_TYPES = new Set(['switch_agent', 'think', 'think_tool', 'bq_chart', 'error']);

const isInlineThinkItem = (item: ISwitchAgentAccordionItem): boolean =>
  INLINE_THINK_TYPES.has(item.type) || (item.type === 'text' && !!item.tool_call_id);

/**
 * 可合并展示的 tool_use.value 白名单。
 * 同时兼容后端返回的英文（read_file）与中文（查看文件内容）value，需与后端约定保持一致。
 * 统一存小写，匹配时转小写比较，兼容英文大小写差异（如 view File Content / Run Skill Script）。
 */
const MERGEABLE_TOOL_USE_VALUES: readonly string[] = [
  'read_file',
  'view file content',
  'run skill script',
  'run_skill_script',
  '查看文件内容',
  '运行 skill 脚本',
];

/**
 * 从 startIndex 收集连续可合并的 tool_use。
 * 已匹配的 think_tool、无 thinkId 的项不打断；其它类型结束本批。
 */
const collectConsecutiveMergeableTools = (
  accordionSource: ISwitchAgentAccordionItem[],
  startIndex: number,
  matchedThinkToolIndexSet: Set<number>,
): { batch: ISwitchAgentAccordionItem[]; lastIdx: number; } => {
  const isMergeable = (item?: ISwitchAgentAccordionItem): boolean => {
    return (
      item?.type === 'tool_use' &&
      MERGEABLE_TOOL_USE_VALUES.includes(
        String(item.value || '').toLowerCase(),
      )
    );
  };
  const startItem = accordionSource[startIndex];
  const batch: ISwitchAgentAccordionItem[] = startItem ? [startItem] : [];
  let lastIdx = startIndex;
  if (!isMergeable(startItem)) {
    return { batch, lastIdx };
  }
  for (
    let nextIndex = startIndex + 1;
    nextIndex < accordionSource.length;
    nextIndex += 1
  ) {
    const nextItem = accordionSource[nextIndex];
    if (!nextItem?.thinkId) {
      continue;
    }
    if (nextItem.type === 'think_tool' && matchedThinkToolIndexSet.has(nextIndex)) {
      continue;
    }
    if (!isMergeable(nextItem)) {
      break;
    }
    batch.push(nextItem);
    lastIdx = nextIndex;
  }
  return { batch, lastIdx };
};

const normalizeToolThinkItems = (
  value: Array<{ type?: string; thinkId?: string; subType?: string; inputIndex?: number; [key: string]: unknown; }> | undefined,
  thinkTimeSeconds?: number | string,
): {
  dataSource: Array<{ type?: string; thinkId?: string; subType?: string; inputIndex?: number; [key: string]: unknown; }>;
  thinkTime: string;
} => {
  const thinkTime = convertSecondsToTimeFormat(String(thinkTimeSeconds || ''));
  try {
    const res = (value || []).filter(
      task =>
        task.type !== 'plan_json' &&
        task.type !== 'current_token_total' &&
        task.type !== 'think_fold_mode',
    );
    if (!res.length) {
      return { dataSource: [], thinkTime };
    }

    if (res.some(item => !item.thinkId)) {
      let currentGroupId = '';
      const processedRes = res.map(item => {
        if (item.type === 'switch_agent') {
          currentGroupId = uuidv4();
          return { ...item, thinkId: currentGroupId };
        }
        return item.thinkId ? item : { ...item, thinkId: currentGroupId };
      });
      return { dataSource: processedRes, thinkTime };
    }

    const groupedMap = res.reduce((map: Map<string, typeof res>, item) => {
      const groupKey = String(item?.thinkId);
      const existingGroup = map.get(groupKey) || [];
      const newGroup =
        item.type === 'switch_agent'
          ? [item, ...existingGroup]
          : [...existingGroup, item];
      return map.set(groupKey, newGroup);
    }, new Map<string, typeof res>());

    return {
      dataSource: Array.from(groupedMap).flatMap(([, items]) => items),
      thinkTime,
    };
  } catch (error) {
    console.error(error);
    return { dataSource: [], thinkTime: '' };
  }
};

interface IUseAccordionDataProps<S = string, N = number> {
  value?: Array<{
    type: S;
    value?: unknown;
    new_agent?: S;
    input?: S;
    inputIndex?: N;
    thinkId?: S;
    tool_call_id?: S;
  }>;
  isLoading: boolean;
  streamingState?: S;
  thinkTimeSeconds?: N;
  messageId?: S;
}

export const useAccordionData = ({
  value,
  isLoading,
  streamingState,
  thinkTimeSeconds,
  messageId,
}: IUseAccordionDataProps) => {
  const { formatMessage } = useIntl();

  const foldMode = useMemo(() => getThinkFoldModeFromValue(value), [value]);

  const tokenTotalBlock = useMemo(
    () => (value || []).find((item: { type?: string; }) => item.type === 'current_token_total') ?? null,
    [value],
  );

  const { dataSource, thinkTime } = useMemo(
    () =>
      isAgentFoldMode(foldMode)
        ? normalizeAgentThinkItems(value, thinkTimeSeconds)
        : normalizeToolThinkItems(value, thinkTimeSeconds),
    [value, thinkTimeSeconds, foldMode],
  );

  const thinkModeBlock = useMemo(
    () => (dataSource?.[0]?.subType === 'think_mode' ? dataSource?.[0] : null),
    [dataSource],
  );

  const plannerBlock = useMemo(() => {
    const startIndex = thinkModeBlock ? 1 : 0;
    const targetItem = dataSource?.[startIndex];
    return targetItem?.type === 'think' && !targetItem?.inputIndex ? targetItem : null;
  }, [dataSource, thinkModeBlock]);

  const accordionSource = useMemo<ISwitchAgentAccordionItem[]>(() => {
    let startIndex = 0;
    if (thinkModeBlock) startIndex += 1;
    if (plannerBlock) startIndex += 1;
    return (dataSource || []).slice(startIndex) as ISwitchAgentAccordionItem[];
  }, [dataSource, thinkModeBlock, plannerBlock]);

  const { accordionData, trailingInlineItems } = useMemo(() => {
    if (!accordionSource?.length) {
      return {
        accordionData: [] as ISwitchAgentAccordionGroup[],
        trailingInlineItems: [] as ISwitchAgentAccordionItem[],
      };
    }

    if (isAgentFoldMode(foldMode)) {
      return resolveAgentAccordionData({
        accordionSource,
        formatMessage,
        streamingState,
        messageId,
      });
    }

    const toolUseThinkValueMap = new Map<string, string[]>();
    const matchedThinkToolIndexSet = new Set<number>();
    const toolUseKeySet = new Set<string>();

    accordionSource.forEach(item => {
      if (item?.type === 'tool_use' && item?.thinkId && item?.tool_call_id) {
        toolUseKeySet.add(getToolUseKey(String(item.thinkId), String(item.tool_call_id)));
      }
    });

    accordionSource.forEach((item, index) => {
      if (item?.type !== 'think_tool' || !item?.thinkId || !item?.tool_call_id) {
        return;
      }
      const key = getToolUseKey(String(item.thinkId), String(item.tool_call_id));
      if (!toolUseKeySet.has(key)) {
        return;
      }
      matchedThinkToolIndexSet.add(index);
      const currentList = toolUseThinkValueMap.get(key) || [];
      toolUseThinkValueMap.set(key, [...currentList, String(item?.value || '')]);
    });

    const toolUseThinkMap = new Map<string, string>();
    toolUseThinkValueMap.forEach((list, key) => {
      toolUseThinkMap.set(key, list.filter(Boolean).join('\n'));
    });

    const isLastToolLoading = (
      item: ISwitchAgentAccordionItem,
      idx: number,
      arrList: ISwitchAgentAccordionItem[],
    ): boolean => {
      const { thinkId } = item;
      const lastToolUseIndex = findLastIndex(arrList, { type: 'tool_use', thinkId });
      const lastThinkIndex = findLastIndex(arrList, { thinkId });
      return lastThinkIndex === lastToolUseIndex && lastToolUseIndex === idx && isLoading;
    };

    const buildToolGroupItems = (
      item: ISwitchAgentAccordionItem,
    ): ISwitchAgentAccordionItem[] => {
      const toolKey = getToolUseKey(String(item.thinkId), String(item.tool_call_id));
      const toolThinkValue = toolUseThinkMap.get(toolKey) || '';
      const items: ISwitchAgentAccordionItem[] = [];

      if (toolThinkValue.trim()) {
        items.push({
          type: 'think_tool',
          thinkId: item.thinkId,
          tool_call_id: item.tool_call_id,
          value: toolThinkValue,
        });
      }

      return items;
    };

    const toolAccordionData: ISwitchAgentAccordionGroup[] = [];
    const pendingInlines: ISwitchAgentAccordionItem[] = [];
    let toolDisplayIndex = 0;
    /** 合并组已消费的下标，forEach 后续项直接跳过。 */
    let skipUntil = -1;

    accordionSource.forEach((item, idx, arrList) => {
      if (!item?.thinkId) return;
      if (idx <= skipUntil) return;

      if (item.type === 'think_tool' && matchedThinkToolIndexSet.has(idx)) {
        return;
      }

      if (isInlineThinkItem(item)) {
        pendingInlines.push({ ...item });
        return;
      }

      if (item.type === 'tool_use') {
        const { batch, lastIdx } = collectConsecutiveMergeableTools(
          accordionSource,
          idx,
          matchedThinkToolIndexSet,
        );
        const shouldMerge = batch.length >= 2;
        // 未合并时 firstItem 即当前 item，可直接用于末位加载态判断
        const firstItem = (shouldMerge ? batch[0] : item) || item;
        const latencyMs = shouldMerge
          ? batch.reduce((sum, tool) => {
            const ms = (tool as { latency?: number; }).latency;
            return sum + (typeof ms === 'number' && ms > 0 ? ms : 0);
          }, 0)
          : (item as { latency?: number; }).latency;
        const isGroupDone = shouldMerge
          ? !isLoading || lastIdx < findLastIndex(arrList, { type: 'tool_use' })
          : !isLastToolLoading(item, idx, arrList);
        const showTime = shouldMerge ? isGroupDone : true;
        const toolTimeText =
          showTime && typeof latencyMs === 'number' && latencyMs > 0
            ? `${formatMessage({ id: 'dba_agent_time_spent' })}${convertSecondsToTimeFormat(String(latencyMs), { unit: 'ms', supportMs: true })}`
            : undefined;
        const mergedTitle = `${formatMessage({ id: 'dba_switch_tool' })}${formatMessage(
          { id: isGroupDone ? 'dba_tools_completed' : 'dba_tools_running' },
          { count: batch.length, tool: String(firstItem.value || '') },
        )}`;

        toolDisplayIndex += 1;
        const toolKey = getToolUseKey(String(firstItem.thinkId), String(firstItem.tool_call_id));
        toolAccordionData.push({
          id: toolKey,
          inlineBeforeItems: pendingInlines.length ? [...pendingInlines] : undefined,
          header: {
            type: 'switch_agent',
            thinkId: toolKey,
            inputIndex: toolDisplayIndex,
            new_agent: shouldMerge
              ? mergedTitle
              : `${formatMessage({ id: 'dba_switch_tool' })}${item.value || ''}`,
            input: toolTimeText,
            done: isGroupDone,
          },
          items: shouldMerge
            ? batch.flatMap(tool => buildToolGroupItems(tool))
            : buildToolGroupItems(item),
        });
        pendingInlines.length = 0;
        if (shouldMerge) skipUntil = lastIdx;
      }
    });

    return {
      accordionData: toolAccordionData,
      trailingInlineItems: [...pendingInlines],
    };
  }, [accordionSource, foldMode, formatMessage, isLoading, streamingState, messageId]);

  return {
    thinkTime,
    thinkModeBlock,
    plannerBlock,
    accordionData,
    trailingInlineItems,
    tokenTotalBlock,
  };
};
